import { Component, ElementRef, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService } from 'src/app/services/api.service';
import { Customer } from 'src/app/model/customer.model';
import * as XLSX from 'xlsx';

@Component({
  selector: 'customer-bulk-upload-modal',
  templateUrl: './customer-bulk-upload.component.html',
  styleUrls: ['./customer-bulk-upload.component.scss']
})
export class CustomerBulkUploadDialog {
  selectedFile: File | null = null;
  isDraggingOver = false;
  uploading = false;
  uploadResult: any = null;
  parsedCustomers: Customer[] = [];
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  constructor(
    public dialogRef: MatDialogRef<CustomerBulkUploadDialog>,
    private apiService: ApiService,
    private snackBar: MatSnackBar
  ) {}

  onNoClick(): void {
    this.dialogRef.close(false);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      this.handleFile(file);
    }
  }

  handleFile(file: File): void {
    const isValid = file.name.endsWith('.csv') || file.name.endsWith('.xls') || file.name.endsWith('.xlsx') ||
      ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'csv'].includes(file.type);
    if (!isValid) {
      this.snackBar.open('Please select a valid CSV, XLS or XLSX file', 'Close', { duration: 3000 });
      this.resetFile();
      return;
    }
    this.selectedFile = file;
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: 'array', cellDates: true });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      this.parsedCustomers = this.parseRows(rows);
      if (this.parsedCustomers.length === 0) {
        this.snackBar.open('No valid customer rows found in file', 'Close', { duration: 3000 });
        this.resetFile();
      } else {
        this.snackBar.open(`${this.parsedCustomers.length} customer(s) parsed`, 'Close', { duration: 2000 });
      }
    };
    reader.readAsArrayBuffer(file);
  }

  parseRows(rows: any[][]): Customer[] {
    if (rows.length < 2) {
      return [];
    }
    const headers = rows[0].map((h: any) => String(h).trim().toLowerCase().replace(/\s+/g, '_'));
    const customers: Customer[] = [];
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      if (!row || row.length === 0) {
        continue;
      }
      const rowMap: any = {};
      headers.forEach((h: string, idx: number) => {
        rowMap[h] = row[idx];
      });
      const customerId = this.toLong(rowMap['cust_id']);
      if (!customerId) {
        continue;
      }
      const customer: any = {
        customerId: customerId,
        customerName: this.toString(rowMap['cust_name']),
        customerPhone: this.toLong(rowMap['cust_phone']),
        customerPhone2: this.toLong(rowMap['cust_phone2']),
        customerPhone3: this.toLong(rowMap['cust_phone3']),
        customerEmail: this.toString(rowMap['cust_email']),
        customerAddress: this.toString(rowMap['cust_address']),
        customerStatus: this.toString(rowMap['cust_status']).toUpperCase(),
        customerJoinedDate: this.formatDate(rowMap['cust_joined_date']),
        customerUnitRate: this.toDouble(rowMap['cust_unit_rate']),
        defaultUnitRate: this.toDouble(rowMap['default_unit_rate']),
        customerDueAmount: this.toDouble(rowMap['cust_due_amount']),
        areaCode: this.toLong(rowMap['area_code']),
        latitude: this.toDouble(rowMap['cust_longitude']),
        longitude: this.toDouble(rowMap['cust_lattitude']),
        rh: this.toDouble(rowMap['rh']),
        wadarta: this.toDouble(rowMap['wadarta']),
        rc: this.toDouble(rowMap['rc']),
        custType: this.toString(rowMap['cust_type']),
        meterId: 0,
        previousReading: 0,
        currentReading: 0,
        areaName: '',
        customerAction: 'add'
      };
      customers.push(customer as Customer);
    }
    return customers;
  }

  onUpload(): void {
    if (!this.parsedCustomers.length) {
      this.snackBar.open('Please select a file with customer data', 'Close', { duration: 3000 });
      return;
    }
    this.uploading = true;
    this.apiService.bulkUploadCustomers(this.parsedCustomers).subscribe({
      next: (result) => {
        this.uploading = false;
        this.uploadResult = result;
        this.resetFile();
        this.snackBar.open(`Upload complete: ${result.savedCount} saved, ${result.skippedCount} skipped, ${result.failedCount} failed`, 'Close', { duration: 5000 });
      },
      error: (err) => {
        this.uploading = false;
        this.snackBar.open('Upload failed: ' + (err.error?.message || err.message), 'Close', { duration: 5000 });
      }
    });
  }

  done(): void {
    this.dialogRef.close(this.uploadResult && this.uploadResult.savedCount > 0);
  }

  uploadAnother(): void {
    this.resetFile();
    this.uploadResult = null;
  }

  private toString(value: any): string {
    if (value === undefined || value === null) {
      return '';
    }
    return String(value).trim();
  }

  private toLong(value: any): number | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    const s = String(value).trim().toLowerCase();
    if (s === 'null') {
      return null;
    }
    const n = Number(s);
    return isNaN(n) ? null : n;
  }

  private toDouble(value: any): number | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    const s = String(value).trim().toLowerCase();
    if (s === 'null') {
      return null;
    }
    const n = Number(s);
    return isNaN(n) ? null : n;
  }

  private formatDate(value: any): string | null {
    if (!value || value === '') {
      return null;
    }
    if (value instanceof Date) {
      const y = value.getFullYear();
      const m = String(value.getMonth() + 1).padStart(2, '0');
      const d = String(value.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    const s = String(value).trim();
    const isoMatch = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
    if (isoMatch) {
      const [_, y, m, d] = isoMatch;
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    const parts = s.split(/[-/]/);
    if (parts.length === 3) {
      const d = parts[0].replace(/\D/g, '');
      const m = parts[1].replace(/\D/g, '');
      const y = parts[2].replace(/\D/g, '');
      if (y.length === 4) {
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
      }
    }
    return null;
  }

  private resetFile(): void {
    this.selectedFile = null;
    this.parsedCustomers = [];
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }
}
